import { z } from "zod";
import { UserId } from "./ids.js";
import { Lang } from "./lang.js";
import { Trade } from "./trade.js";
/**
 * The device capability number that understands `colleague`, `colleague_note` and `referral` envelopes. Linking in
 * person and referrals are gone from the app; the kinds stay so a phone on an older build can still be read: an
 * `ask`, an `ok` or a `referral` from one is dropped on arrival, while `end`, `renew` and notes carry mentor links.
 */
export const COLLEAGUE_PROTOCOL = 2;
/** At most this many trades or languages travel on a colleague card. */
const MAX_CARD_ITEMS = 5;
const shortText = z.string().min(1).max(300);
/** The code a colleague QR carries: 16 random bytes, base64url without padding. */
export const LinkCode = z.string().regex(/^[A-Za-z0-9_-]{22}$/, "link code");
/** What two linked colleagues know about each other: a name, what they do and the languages they speak. */
export const ColleagueCard = z.object({
    name: z.string().min(1).max(80),
    trades: z.array(Trade).max(MAX_CARD_ITEMS),
    languages: z.array(Lang).max(MAX_CARD_ITEMS),
});
/**
 * The steps of a link: the scanner asks (with the code it scanned), the shower answers ok, either side ends it.
 * `renew` (mentor links only, so only between phones at `MENTOR_PROTOCOL`) moves the link's expiry to `expires_at`.
 */
export const LinkStep = z.enum(["ask", "ok", "end", "renew"]);
/** `colleague`: one step of linking two colleagues met in person, or ending the link. */
export const ColleagueLink = z
    .object({
    step: LinkStep,
    code: LinkCode.optional(),
    card: ColleagueCard.optional(),
    expires_at: z.iso.datetime({ offset: true }).optional(),
})
    .refine((b) => b.step !== LinkStep.enum.ask || (b.code !== undefined && b.card !== undefined), "ask needs code and card")
    .refine((b) => b.step !== LinkStep.enum.ok || b.card !== undefined, "ok needs a card")
    .refine((b) => b.step !== LinkStep.enum.renew || b.expires_at !== undefined, "renew needs expires_at");
/** The longest colleague note, in characters. */
export const COLLEAGUE_NOTE_MAX = 300;
/** `colleague_note`: a message between linked colleagues, tagged with the language it is in. */
export const ColleagueNote = z.object({ text: z.string().min(1).max(COLLEAGUE_NOTE_MAX), lang: Lang });
/** `referral`: worker → household, on a request the worker declined: a colleague the household may ask instead. */
export const Referral = z.object({ colleague: UserId, card: ColleagueCard, note: shortText.optional() });
//# sourceMappingURL=colleague.js.map